import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { HttpException } from '../../exceptions/index';
import { ApiService } from '../../services/index';
import { Scenario } from '../../types/scenario-modeling.types';
import { useAppQuery } from '../use-app-query.hook';

export const useScenarios = ({
  companyId,
  numberOfFetchedItems = 100,
  currentPage = 1,
  search,
}: {
  companyId: string;
  currentPage?: number;
  numberOfFetchedItems?: number;
  search: string;
}) => {
  const queryClient = useQueryClient();
  const skip = (currentPage - 1 <= 0 ? 0 : currentPage - 1) * numberOfFetchedItems;
  const { data, isLoading, isPending, isFetching, refetch, isFetched } = useAppQuery<
    { data: Scenario[]; totalPages: number },
    HttpException
  >({
    queryKey: [
      QueryKey.GET_SCENARIOS,
      companyId,
      {
        page: currentPage,
        skip,
        numberOfFetchedItems,
      },
    ],
    staleTime: 0,
    queryFn: () =>
      ApiService.get(
        { endpoint: BackendRoute.SCENARIOS },
        {
          queryParams: {
            take: numberOfFetchedItems,
            skip,
            search,
          },
        },
      ),
    enabled: !!companyId,
  });

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SCENARIOS] });
  }, [queryClient]);

  return {
    invalidateQuery,
    scenarios: data?.data,
    isLoading: isLoading || isPending || isFetching,
    isFetched,
    refetch,
  };
};
